import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Mic, Square, Camera, MessageSquareText, Keyboard, Sparkles,
  Plus, Trash2, AlertTriangle, Loader2,
} from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import ConfirmAction from "@/components/ConfirmAction";
import { Card, Button, Chip, Toast } from "@/ui";
import "@/storefront/storefront.css";

const CATEGORIES = ["Meals", "Snacks", "Drinks", "Desserts", "Add-ons", "Bundles", "Other"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const METHODS = [
  { key: "voice", label: "Voice note", hint: "Say your menu out loud", Icon: Mic, big: true },
  { key: "photo", label: "Photo", hint: "Snap your menu board", Icon: Camera },
  { key: "posts", label: "My posts", hint: "Tap what you sell", Icon: MessageSquareText },
  { key: "type", label: "Type it", hint: "halo-halo 120, buko 110", Icon: Keyboard },
];

export default function AddProducts() {
  const { shopId, shopName } = useAuth();
  const navigate = useNavigate();
  const [method, setMethod] = useState("voice");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [needsAi, setNeedsAi] = useState("");
  const [items, setItems] = useState(null);       // null = no preview yet
  const [existingCats, setExistingCats] = useState([]);
  useEffect(() => {
    if (!shopId) return;
    api.get(`/categories?shopId=${shopId}`).then((r) => setExistingCats((r.data.categories || []).map((c) => c.name))).catch(() => {});
  }, [shopId]);
  const [confirm, setConfirm] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [toast, setToast] = useState("");

  // voice
  const [recording, setRecording] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const photoRef = useRef(null);
  const voiceFileRef = useRef(null);

  // posts + type
  const [topics, setTopics] = useState(null);
  const [picked, setPicked] = useState([]);
  const [text, setText] = useState("");

  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 2600); };

  const applyPreview = (data) => {
    if (data.needsAi) { setNeedsAi(data.message); setItems(null); return; }
    setNeedsAi("");
    const rows = (data.items || []).map((it) => ({
      name: it.name, pesos: it.priceCentavos ? (it.priceCentavos / 100).toString() : "",
      description: it.description || "", category: it.category || "Other",
      unclear: it.unclear, question: it.question || "",
    }));
    setItems(rows);
    if (!rows.length) flash(data.message || "Nothing found — try another way.");
  };

  const runForm = async (url, form) => {
    setBusy(true); setError(""); setNeedsAi("");
    try {
      const r = await fetch(url, { method: "POST", body: form, credentials: "include" });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || "read");
      applyPreview(data);
    } catch (e) { setError(e.message === "read" ? "We couldn't read that. Try again or use another way." : e.message); }
    finally { setBusy(false); }
  };

  const runJson = async (path, body) => {
    setBusy(true); setError(""); setNeedsAi("");
    try { const r = await api.post(path, { shopId, ...body }); applyPreview(r.data); }
    catch (e) { setError(friendlyError(e)); }
    finally { setBusy(false); }
  };

  // --- voice recording ---
  const startRec = async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        const form = new FormData();
        form.append("file", blob, "voice.webm");
        runForm(`${BACKEND}/api/products/intake/voice?shopId=${shopId}`, form);
      };
      mediaRef.current = mr; mr.start(); setRecording(true);
    } catch { setError("We couldn't use the microphone. You can upload a voice file instead."); }
  };
  const stopRec = () => { setRecording(false); mediaRef.current?.stop(); };

  const onPhoto = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const form = new FormData(); form.append("file", f);
    runForm(`${BACKEND}/api/products/intake/photo?shopId=${shopId}`, form);
    if (photoRef.current) photoRef.current.value = "";
  };
  const onVoiceFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const form = new FormData(); form.append("file", f);
    runForm(`${BACKEND}/api/products/intake/voice?shopId=${shopId}`, form);
    if (voiceFileRef.current) voiceFileRef.current.value = "";
  };

  const loadTopics = async () => {
    setBusy(true); setError("");
    try { const r = await api.get(`/products/intake/post-topics?shopId=${shopId}`); setTopics(r.data.topics || []); }
    catch (e) { setError(friendlyError(e)); }
    finally { setBusy(false); }
  };
  const toggleTopic = (t) => setPicked((p) => p.includes(t) ? p.filter((x) => x !== t) : [...p, t]);

  const pickMethod = (k) => {
    setMethod(k); setItems(null); setNeedsAi(""); setError(""); setPicked([]);
    if (k === "posts" && topics === null) loadTopics();
  };

  // --- preview editing ---
  const setRow = (i, patch) => setItems((rows) => rows.map((r, n) => n === i ? { ...r, ...patch } : r));
  const removeRow = (i) => setItems((rows) => rows.filter((_, n) => n !== i));
  const addRow = () => setItems((rows) => [...(rows || []), { name: "", pesos: "", description: "", category: "Other", unclear: false }]);

  const readyItems = (items || []).filter((r) => r.name.trim() && parseFloat(r.pesos || 0) > 0);
  const commit = async () => {
    setCommitting(true); setError("");
    try {
      const payload = readyItems.map((r) => ({
        name: r.name.trim(), priceCentavos: Math.round(parseFloat(r.pesos) * 100),
        description: r.description, category: r.category,
      }));
      const r = await api.post("/products/intake/commit", { shopId, items: payload });
      flash(`added ${r.data.created} item${r.data.created === 1 ? "" : "s"}`);
      setTimeout(() => navigate("/products"), 900);
    } catch (e) { setError(friendlyError(e)); setCommitting(false); setConfirm(false); }
  };

  return (
    <Layout shopName={shopName}>
      <div className="row" style={{ marginBottom: 16 }}>
        <button className="btn btn-ghost btn-sm" data-testid="add-back" onClick={() => navigate("/products")}>
          <ArrowLeft size={20} strokeWidth={1.75} />back to items
        </button>
      </div>
      <h1 className="t-h1" style={{ marginBottom: 4 }} data-testid="add-products-page">add items</h1>
      <p className="muted" style={{ marginBottom: 20, maxWidth: 640 }}>
        Add your items whichever way is easiest. Everything ends in one list you check and fix — nothing is saved until you say so.
      </p>
      {error ? <div className="err-text" data-testid="add-error" style={{ marginBottom: 12 }}>{error}</div> : null}

      {/* method picker */}
      <div className="grid grid-2" style={{ gap: 12, marginBottom: 20 }}>
        {METHODS.map((m) => (
          <button key={m.key} data-testid={`method-${m.key}`} onClick={() => pickMethod(m.key)}
            className="pick-row" aria-pressed={method === m.key}
            style={method === m.key ? { boxShadow: "inset 0 0 0 2px var(--accent)", borderColor: "var(--accent)" } : undefined}>
            <span className="row" style={{ gap: 12 }}>
              <span className="ph-icon" style={{ margin: 0 }}><m.Icon size={20} strokeWidth={1.75} /></span>
              <span>
                <span className="t-body" style={{ display: "block", fontWeight: 600 }}>{m.label}</span>
                <span className="muted t-small">{m.hint}</span>
              </span>
            </span>
            {m.big ? <Chip tone="good" dot>popular</Chip> : null}
          </button>
        ))}
      </div>

      {/* method input */}
      <Card style={{ marginBottom: 16 }}>
        {method === "voice" && (
          <div className="stack" style={{ gap: 12, alignItems: "center", textAlign: "center" }}>
            <p className="t-body">Tap, then say your menu — for example: <em>"halo-halo 120, mais con yelo 90, buko pandan 110"</em>. Tagalog, English or mixed is fine.</p>
            {!recording ? (
              <Button variant="primary" size="lg" data-testid="voice-record-btn" onClick={startRec} disabled={busy}>
                <Mic size={18} strokeWidth={1.75} /> Start recording
              </Button>
            ) : (
              <Button variant="danger" size="lg" data-testid="voice-stop-btn" onClick={stopRec}>
                <Square size={18} strokeWidth={1.75} /> Stop &amp; read
              </Button>
            )}
            <div>
              <button className="link-btn" onClick={() => voiceFileRef.current?.click()} data-testid="voice-upload">or upload a voice file</button>
              <input ref={voiceFileRef} type="file" accept="audio/*" data-testid="voice-file-input" style={{ display: "none" }} onChange={onVoiceFile} />
            </div>
          </div>
        )}
        {method === "photo" && (
          <div className="stack" style={{ gap: 12, alignItems: "center", textAlign: "center" }}>
            <p className="t-body">Take a clear, straight photo of your menu board, tarpaulin or price list.</p>
            <Button variant="primary" size="lg" data-testid="photo-take-btn" onClick={() => photoRef.current?.click()} disabled={busy}>
              <Camera size={18} strokeWidth={1.75} /> Take / choose photo
            </Button>
            <input ref={photoRef} type="file" accept="image/*" capture="environment" data-testid="photo-file-input" style={{ display: "none" }} onChange={onPhoto} />
          </div>
        )}
        {method === "posts" && (
          <div className="stack" style={{ gap: 12 }}>
            <p className="t-body">Here's what you post about most. Tap the ones you actually sell.</p>
            <div className="row" style={{ gap: 8, flexWrap: "wrap" }} data-testid="posts-topics">
              {(topics || []).map((t, i) => (
                <button key={t} data-testid={`topic-chip-${i}`} onClick={() => toggleTopic(t)}
                  className="swatch-btn" style={picked.includes(t) ? { boxShadow: "inset 0 0 0 2px var(--accent)" } : undefined}>
                  {t}
                </button>
              ))}
              {topics && topics.length === 0 ? <span className="muted t-small">No posts to read yet.</span> : null}
            </div>
            <Button variant="primary" data-testid="posts-use-btn" disabled={!picked.length || busy}
              onClick={() => runJson("/products/intake/posts", { selected: picked })}>
              Use these {picked.length ? `(${picked.length})` : ""}
            </Button>
          </div>
        )}
        {method === "type" && (
          <div className="stack" style={{ gap: 12 }}>
            <p className="t-body">Type your items however comes naturally — one per line or separated by commas.</p>
            <textarea className="input" data-testid="text-input" rows={5} value={text}
              onChange={(e) => setText(e.target.value)} placeholder={"halo-halo 120\nmais con yelo 90\nbuko pandan 110"} />
            <Button variant="primary" data-testid="text-read-btn" disabled={busy || !text.trim()}
              onClick={() => runJson("/products/intake/text", { text })}>Read this</Button>
          </div>
        )}
        {busy ? <div className="row" style={{ gap: 8, justifyContent: "center", marginTop: 12 }}><Loader2 size={18} className="spin" /> <span className="muted">reading…</span></div> : null}
      </Card>

      {needsAi ? (
        <Card data-testid="needs-ai-notice" className="row" style={{ gap: 12, borderColor: "var(--warn)", marginBottom: 16 }}>
          <Sparkles size={20} style={{ color: "var(--warn)" }} />
          <div style={{ flex: 1 }}>
            <div className="t-body">{needsAi}</div>
            <button className="link-btn" onClick={() => navigate("/platform-connections")} data-testid="go-connections">Open Connections</button>
          </div>
        </Card>
      ) : null}

      {/* shared preview */}
      {items ? (
        <>
          <div className="row-between" style={{ marginBottom: 8 }}>
            <h2 className="t-h2">check your list</h2>
            <Button size="sm" data-testid="add-row-btn" onClick={addRow}><Plus size={14} /> add a row</Button>
          </div>
          <div className="stack" style={{ gap: 10 }} data-testid="preview-list">
            {items.map((r, i) => (
              <Card key={i} data-testid={`preview-row-${i}`} style={r.unclear && !(parseFloat(r.pesos || 0) > 0) ? { borderColor: "var(--warn)" } : undefined}>
                <div className="grid" style={{ gridTemplateColumns: "1fr 110px", gap: 10 }}>
                  <input className="input" data-testid={`row-name-${i}`} value={r.name} placeholder="item name"
                    onChange={(e) => setRow(i, { name: e.target.value })} />
                  <input className="input num" data-testid={`row-price-${i}`} inputMode="decimal" value={r.pesos} placeholder="price"
                    onChange={(e) => setRow(i, { pesos: e.target.value.replace(/[^\d.]/g, "") })} />
                </div>
                <div className="grid" style={{ gridTemplateColumns: "150px 1fr auto", gap: 10, marginTop: 10, alignItems: "center" }}>
                  <select className="select" data-testid={`row-category-${i}`} value={r.category} onChange={(e) => setRow(i, { category: e.target.value })}>
                    {Array.from(new Set([...(existingCats || []), ...CATEGORIES, r.category].filter(Boolean))).map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <input className="input" data-testid={`row-desc-${i}`} value={r.description} placeholder="short description"
                    onChange={(e) => setRow(i, { description: e.target.value })} />
                  <button className="btn btn-ghost btn-sm" data-testid={`row-remove-${i}`} onClick={() => removeRow(i)} aria-label="remove"><Trash2 size={16} /></button>
                </div>
                {r.unclear && r.question && !(parseFloat(r.pesos || 0) > 0) ? (
                  <div className="row" style={{ gap: 6, marginTop: 8, color: "var(--warn)" }} data-testid={`row-question-${i}`}>
                    <AlertTriangle size={14} /><span className="t-small">{r.question}</span>
                  </div>
                ) : null}
              </Card>
            ))}
            {items.length === 0 ? <p className="muted">No items yet — add a row or try another way.</p> : null}
          </div>
          <div className="row-between" style={{ marginTop: 16 }}>
            <p className="muted t-small">{readyItems.length} ready · items still needing a price are skipped.</p>
            <Button variant="primary" data-testid="intake-commit-btn" disabled={!readyItems.length} onClick={() => setConfirm(true)}>
              save {readyItems.length} item{readyItems.length === 1 ? "" : "s"}
            </Button>
          </div>
        </>
      ) : null}

      <ConfirmAction
        open={confirm}
        title={`Save ${readyItems.length} item${readyItems.length === 1 ? "" : "s"}?`}
        preview={[
          `${readyItems.length} item${readyItems.length === 1 ? "" : "s"} will be created in your shop.`,
          "Items without a price are skipped — you can add them later.",
          "You can edit or hide any item afterwards.",
        ]}
        confirmLabel="Yes, save them" busy={committing} error={error}
        onConfirm={commit} onCancel={() => setConfirm(false)}
      />
      {toast ? <div className="toast-wrap"><Toast tone="good" data-testid="add-toast">{toast}</Toast></div> : null}
    </Layout>
  );
}
